import { getEmployeesByManagerApi } from "../../Redux/Services";

// Persona = who this login is, for succession/scoring access.
//   "hr"       -> full app (HRBP view), unchanged experience.
//   "manager"  -> restricted "My Team -> Score" view only (leak-proof).
//   "employee" -> no scoring access (normal Spotlight).
export type Persona = "hr" | "manager" | "employee";

// Same HR-role check already used in ReportingManagerTable.tsx.
export const isHRRole = (role: string | null): boolean => {
  if (!role || role === "undefined") return false;
  return (
    role.includes("hr") ||
    role.includes("employee-db-hrmanager") ||
    role.includes("employee-db-hrbp") ||
    role.includes("hr-ta")
  );
};

// The manager's extra sidebar entry / scoped scoring page.
export const MANAGER_RESOURCE_PATH = "/manager/resource-allocation";

export const getPersona = (): Persona | null => {
  const value = localStorage.getItem("persona");
  if (value === "hr" || value === "manager" || value === "employee") {
    return value;
  }
  return null;
};

// Called once right after login (role + login already in localStorage).
// HR wins: if they are HR we never treat them as a manager.
export const computeAndStorePersona = async (): Promise<Persona> => {
  const role = localStorage.getItem("role");
  if (isHRRole(role)) {
    localStorage.setItem("persona", "hr");
    return "hr";
  }

  let persona: Persona = "employee";
  let reporteeIds: string[] = [];
  try {
    const loginRaw = localStorage.getItem("login");
    const loginId = loginRaw ? JSON.parse(loginRaw)?.id : null;
    if (loginId) {
      const res = await getEmployeesByManagerApi(loginId);
      const reportees = res?.data;
      if (Array.isArray(reportees) && reportees.length > 0) {
        persona = "manager";
        reporteeIds = reportees.map((r: any) => r?._id).filter(Boolean);
      }
    }
  } catch (e) {
    // On any failure, fall back to the safest persona (no elevated access).
    persona = "employee";
    reporteeIds = [];
  }

  localStorage.setItem("persona", persona);
  // Cache the reportee id set so the succession pages can scope their people
  // lists to the manager's own team without re-fetching on every page.
  try {
    localStorage.setItem("reporteeIds", JSON.stringify(reporteeIds));
  } catch (e) {
    /* ignore */
  }
  return persona;
};

// True only for a pure-manager persona (used to switch pages into team scope).
export const isManagerPersona = (): boolean => getPersona() === "manager";

// The manager's reportee employee _ids, as a Set for O(1) membership checks.
// Empty set when not a manager or nothing cached.
export const getReporteeIdSet = (): Set<string> => {
  try {
    const raw = localStorage.getItem("reporteeIds");
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch (e) {
    return new Set();
  }
};
